import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ENV } from "@app/constants/env"
import { MstrUserEntity, UserDetailsByIdDto, BaseDto, UserCountryAccessEntity, UserExistsDto, RegisteredUserEntity, UserFields } from '@feature/types/user.types';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getToken } from '@app/helpers/authProvider';

const rawBaseQuery = fetchBaseQuery({
    baseUrl: ENV.BASE_API_URL +ENV.USER_APIURL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    incomingArgs,
    api,
    extraOptions
) => {
    let args: string | FetchArgs = incomingArgs;

    // normalize string arg to object so we can set headers
    if (typeof args === 'string') {
        args = { url: args };
    }

    try {
        const token = await getToken(); // ensure this returns a non-empty string
        //const token = sessionStorage.getItem('accessToken'); // Replace 'authToken' with the actual key used in sessionStorage
        if (token) {
            const headers =
                (args as FetchArgs).headers instanceof Headers
                    ? (args as FetchArgs).headers
                    : new Headers((args as FetchArgs).headers);

            headers.set('Authorization', `Bearer ${token}`); // use standard casing
            (args as FetchArgs).headers = headers;
        }

        const result = await rawBaseQuery(args, api, extraOptions);

        // optional: retry once if unauthorized (adjust getToken refresh logic if available)
        if (result.error && result.error.status === 401 && token) {
            // If you have a refresh flow, invoke it here then retry.
            // const refreshed = await getRefreshedToken();
            // if (refreshed) { headers.set('Authorization', `Bearer ${refreshed}`); return rawBaseQuery(args, api, extraOptions); }
        }
        return result;
    } catch {
        return { error: { status: 500, data: 'Token retrieval failed' } } as any;
    }
};

export interface ApiResponse<T> {
    success: boolean
    message?: string
    data?: T
    error?: string
}
export const usersliceApi = createApi({
    reducerPath: 'usersliceApi',
    baseQuery: authBaseQuery,
    tagTypes: ['usermanagement'],
    endpoints: (builder) => ({

        createUser: builder.mutation<string, { userDto: MstrUserEntity }>({
            query: ({ userDto }) => ({
                url: `User/CreateUser`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: userDto,
            }),
            invalidatesTags: ['usermanagement'],
        }),
        createUserWithKeyCloak: builder.mutation<string, { userDto: MstrUserEntity }>({
            query: ({ userDto }) => ({
                url: `User/CreateUserWithKeycloak`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: userDto,
            }),
            invalidatesTags: ['usermanagement'],
        }),
        UpdateUser: builder.mutation<string, { userDto: MstrUserEntity }>({
            query: ({ userDto }) => ({
                url: `User/UpdateUser`,
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: userDto,
            }),
            invalidatesTags: ['usermanagement'],
        }),
        deleteUser: builder.mutation<ApiResponse<null>, { mstr_user_id: number }>(
            {
                query: ({ mstr_user_id }) => ({
                    url: `User/DeleteUser/${mstr_user_id}`,
                    method: 'PUT',
                }),
                invalidatesTags: ['usermanagement'],
            }
        ),
        getUserById: builder.query<MstrUserEntity[], { dto: UserDetailsByIdDto }>({
            query: ({ dto }) => ({
                url: `User/GetUserById`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: dto,
            }),
            providesTags: ['usermanagement'],
        }),
        getAllUsers: builder.query<MstrUserEntity[], { dto: BaseDto }>({
            query: ({ dto }) => ({
                url: `User/GetUsers`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: dto,
            }),
            providesTags: ['usermanagement'],
        }),
        getUserCountryAccess: builder.query<UserCountryAccessEntity[], number>({
            query: (mstrUserId) => ({
                url: `User/GetUserCountryAccess/${mstrUserId}`,
                method: 'GET',
            }),
            providesTags: ['usermanagement'],
        }),
        AddUserCountryAccess: builder.mutation<ApiResponse<UserCountryAccessEntity>, { userCountryDto: UserCountryAccessEntity }>({
            query: ({ userCountryDto }) => ({
                url: `User/AddUserCountryAccess`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: userCountryDto,
            }),
            invalidatesTags: ['usermanagement'],
        }),
        deleteUserCountryAccess: builder.mutation<ApiResponse<null>, { empCountryId: number }>(
            {
                query: ({ empCountryId }) => ({
                    url: `User/DeleteUserCountryAccess/${empCountryId}`,
                    method: 'POST',
                }),
                invalidatesTags: ['usermanagement'],
            }
        ),
        UpdateUserCountry: builder.mutation<ApiResponse<UserCountryAccessEntity>, { userCountryDto: UserCountryAccessEntity }>({
            query: ({ userCountryDto }) => ({
                url: `User/UpdateUserCountry`,
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: userCountryDto,
            }),
            invalidatesTags: ['usermanagement'],
        }),
        userExists: builder.query<boolean, UserExistsDto>({
            query: (dto) => ({
                url: `User/UserExists`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: dto,
            }),
            providesTags: ['usermanagement'],
        }),
        getAllRegisteredUsers: builder.query<RegisteredUserEntity[], { dto: BaseDto }>({
            query: ({ dto }) => ({
                url: `User/GetNonConfirmedUsers`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: dto,
            }),
            providesTags: ['usermanagement'],
        }),
        setUserField: builder.mutation<UserFields, { fields: UserFields }>({
            query: ({ fields }) => ({
                url: `UserFields/SetUserField`,
                method: 'POST',
                // headers: {
                //     'Content-Type': 'application/json',
                // },
                body: fields,
            }),
            invalidatesTags: ['usermanagement'],
        }),
        getUserFields: builder.query<UserFields[], { userIndex: number; fieldType: string; showId?: number }>({
            query: ({ userIndex, fieldType, showId }) => {
                const url = showId ? `UserFields/GetUserFields/${userIndex}/${fieldType}/${showId}` : `UserFields/GetUserFields/${userIndex}/${fieldType}/0`;
                return {
                    url,
                    method: 'GET',
                };
            },
            providesTags: ['usermanagement'],
        }),

    }),
});

export const {
    useCreateUserMutation,
    useCreateUserWithKeyCloakMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useGetUserByIdQuery,
    useGetAllUsersQuery,
    useGetUserCountryAccessQuery,
    useAddUserCountryAccessMutation,
    useDeleteUserCountryAccessMutation,
    useUpdateUserCountryMutation,
    useUserExistsQuery,
    useLazyUserExistsQuery,
    useGetAllRegisteredUsersQuery,
    useSetUserFieldMutation,
    useGetUserFieldsQuery,
    useLazyGetUserFieldsQuery,
} = usersliceApi;
